import { CdkMenu, CdkTargetMenuAim } from '@angular/cdk/menu';
import { afterNextRender, Directive, ElementRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { deriveMenuSideFromTransformOrigin, MENU_SIDE, type MenuSide } from '@spartan-ng/brain/core';
import { classes } from '@lite-workspace/shared/helm-ui/utils';
import { DROPDOWN_MENU_SURFACE } from './hlm-dropdown-menu';

@Directive({
  selector: '[hlmDropdownMenuSub],hlm-dropdown-menu-sub',
  // Same-element requirement as the root menu — see HlmDropdownMenu. A sub-menu that itself owns
  // sub-menus needs its own aim.
  hostDirectives: [CdkMenu, CdkTargetMenuAim],
  host: {
    'data-slot': 'dropdown-menu-sub',
    '[attr.data-state]': '_state()',
    '[attr.data-side]': '_side()',
  },
})
export class HlmDropdownMenuSub {
  private readonly _host = inject(CdkMenu);
  private readonly _elementRef = inject(ElementRef<HTMLElement>);
  // The sub-trigger provides its configured side; CDK parents this content's injector under it.
  private readonly _menuSide = inject(MENU_SIDE, { optional: true });

  protected readonly _state = signal('open');
  protected readonly _side = signal<MenuSide>(this._menuSide?.side() ?? 'right');

  constructor() {
    classes(() => DROPDOWN_MENU_SURFACE);

    this.setSideFromTransformOrigin();
    // ENTER animates; EXIT cannot — CDK detaches the overlay synchronously after `closed`.
    // See HlmDropdownMenu for the full reason.
    this._host.closed.pipe(takeUntilDestroyed()).subscribe(() => this._state.set('closed'));
  }

  private setSideFromTransformOrigin(): void {
    const side = this._menuSide?.side() ?? 'right';
    // Read CDK's attach-time transform-origin before paint, so the menu shows its final side without a flip.
    afterNextRender(() => {
      this._side.set(deriveMenuSideFromTransformOrigin(this._elementRef.nativeElement.style.transformOrigin, side));
    });
  }
}
